import apiBaseState from "~/constants/apiBaseState";
import Vueset from "~/utils/Vueset";

const getInitialReelsQuery = () => ({
    page: 1,
    length: 10,
    column: 'created_at',
    direction: 'desc'
});

const getState = () => ({
    getReelsState: apiBaseState(),
    getReelState: apiBaseState(),
    createReelState: apiBaseState(),
    deleteReelState: apiBaseState(),
    updateReelState: apiBaseState(),
    getReelsQuery: getInitialReelsQuery(),
    // New states for Clip operations
    uploadClipState: apiBaseState(),
    attachClipState: apiBaseState(),
    updateClipState: apiBaseState(),
    deleteClipState: apiBaseState(),
    saveReelState: apiBaseState(),
});

export const state = () => getState();

export const mutations = {
    RESET_STORE(state) {
        Object.assign(state, getState());
    },
    SET_SAVE_REEL(state, payload) {
        state.saveReelState = payload;
    },
    RESET_REELS_QUERY(state, payload) {
        state.getReelsQuery = getInitialReelsQuery();
    },
    SET_GET_REELS_QUERY(state, payload) {
        state.getReelsQuery = payload;
    },
    SET_GET_REELS(state, payload) {
        state.getReelsState = payload;
    },
    SET_GET_REEL(state, payload) {
        state.getReelState = payload;
    },
    SET_CREATE_REEL(state, payload) {
        state.createReelState = payload;
    },
    SET_UPDATE_REEL(state, payload) {
        state.updateReelState = payload;
    },
    SET_DELETE_REEL(state, payload) {
        state.deleteReelState = payload;
    },
    PUSH_REEL(state, payload) {
        if (!state.getReelsState.data) {
            Vueset(state.getReelsState, 'data', { data: [] });
        } else if (!Array.isArray(state.getReelsState.data.data)) {
            Vueset(state.getReelsState.data, 'data', []);
        }
        state.getReelsState.data.data.push(payload);
    },
    UPDATE_REEL_BY_ID(state, payload) {
        const reelsData = state.getReelsState?.data?.data;
        if (Array.isArray(reelsData)) {
            const reelIndex = reelsData.findIndex(reel => reel.id === payload.id);
            if (reelIndex !== -1) {
                Vueset(state.getReelsState.data.data, reelIndex, payload);
            }
        }

        if (
            state.getReelState?.data?.id === payload.id &&
            payload.name !== undefined
        ) {
            Vueset(state.getReelState.data, 'name', payload.name);
        }
    },
    UPDATE_REEL_DATA_BY_ID(state, payload) {
        state.getReelState.data = payload;
    },
    DELETE_REEL_BY_ID(state, reelId) {
        const reelsData = state.getReelsState?.data?.data;
        if (Array.isArray(reelsData)) {
            const reelIndex = reelsData.findIndex(reel => reel.id === reelId);
            if (reelIndex !== -1) {
                state.getReelsState.data.data.splice(reelIndex, 1);
            }
        }
    },
    // Mutations for Clip operations
    SET_UPLOAD_CLIP(state, payload) {
        state.uploadClipState = payload;
    },
    SET_ATTACH_CLIP(state, payload) {
        state.attachClipState = payload;
    },
    SET_UPDATE_CLIP(state, payload) {
        state.updateClipState = payload;
    },
    SET_DELETE_CLIP(state, payload) {
        state.deleteClipState = payload;
    },
    ADD_CLIP_TO_REEL(state, { reelId, clip }) {
        const reelsData = state.getReelsState?.data?.data;
        if (Array.isArray(reelsData)) {
            const reel = reelsData.find(r => r.id === parseInt(reelId));
            if (reel) {
                // Initialize library_clips if it doesn't exist
                if (!reel.library_clips) {
                    Vueset(reel, 'library_clips', []);
                }
                // Prevent duplicate entries (extra safety)
                const exists = reel.library_clips.some(existingClip => existingClip.path === clip.path);
                if (!exists) {
                    reel.library_clips.push(clip);
                }
            }
        }
    },
    UPDATE_CLIP_BY_ID(state , { reelId, clipId, data }) {
        const reelsData = state.getReelState?.data;
            // Find the reel with the given reelId (convert reelId to number if needed)

                // Ensure the reel has a library_clips array
                if (Array.isArray(reelsData.library_clips)) {
                    // Find the index of the clip within library_clips
                    const clipIndex = reelsData.library_clips.findIndex(clip => clip.id === clipId || clip.id === parseInt(clipId));
                    if (clipIndex !== -1) {
                        // Update the clip at the found index
                        Vueset(reelsData.library_clips, clipIndex, data);
                    }
                }
        }
};

export const actions = {
    GET_REELS({ state }, { accountSlug, projectSlug }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels`;
        return this.$whyApi.get({
            url,
            params: state.getReelsQuery,
            mutation: "reels/SET_GET_REELS",
            errorMessage: "Error getting reels",
            notify: this.$notify
        });
    },
    GET_REEL({ }, { accountSlug, projectSlug, reelId }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}`;
        return this.$whyApi.get({
            url,
            mutation: "reels/SET_GET_REEL",
            errorMessage: "Error getting reel",
            notify: this.$notify
        });
    },
    async CREATE_REEL({ commit }, { accountSlug, projectSlug, name }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels`;
        const response = await this.$whyApi.post({
            url,
            data: { name },
            mutation: "reels/SET_CREATE_REEL",
            errorMessage: "Error creating reel",
            notify: this.$notify
        });
        commit('PUSH_REEL', response.data);
        return response;
    },
    async DELETE_REEL({ commit }, { accountSlug, projectSlug, reelId }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}`;
        const response = await this.$whyApi.delete({
            url,
            mutation: "reels/SET_DELETE_REEL",
            errorMessage: "Error deleting reel",
            notify: this.$notify
        });
        commit('DELETE_REEL_BY_ID', reelId);
        return response;
    },
    async UPDATE_REEL({ commit }, { accountSlug, projectSlug, name, reelId }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}`;
        const response = await this.$whyApi.put({
            url,
            data: { name },
            mutation: "reels/SET_UPDATE_REEL",
            errorMessage: "Error updating reel",
            notify: this.$notify
        });
        commit('UPDATE_REEL_BY_ID', response.data);
        return response;
    },
    async SAVE_REEL({ commit }, { accountSlug, projectSlug, reelId, payload }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}/clips`;
        const response = await this.$whyApi.post({
            url,
            data: payload,
            mutation: "reels/SET_SAVE_REEL",
            errorMessage: "Error saving reel",
            notify: this.$notify
        });
        commit('UPDATE_REEL_BY_ID', response.data);
        return response;
    },
    // Actions for Clip operations
    async UPLOAD_CLIP({ commit }, { accountSlug, projectSlug, reelId, data }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}/clips/upload`;
        const response = await this.$whyApi.post({
            url,
            data,
            mutation: "reels/SET_UPLOAD_CLIP",
            errorMessage: "Error uploading clip",
            notify: this.$notify
        });
        return response;
    },
    async ATTACH_CLIP({ commit }, { accountSlug, projectSlug, reelId, data }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}/clips/attach`;
        const response = await this.$whyApi.post({
            url,
            data,
            mutation: "reels/SET_ATTACH_CLIP",
            errorMessage: "Error attaching clip",
            notify: this.$notify
        });
        return response;
    },
    async UPDATE_CLIP({ commit }, { accountSlug, projectSlug, reelId, clipId, data }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}/clips/${clipId}`;
        const response = await this.$whyApi.put({
            url,
            data,
            mutation: "reels/SET_UPDATE_CLIP",
            errorMessage: "Error updating clip",
            notify: this.$notify
        });
        commit('UPDATE_CLIP_BY_ID', {
            reelId,
            clipId,
            data: response.data
        });
        return response;
    },
    async DELETE_CLIP({ commit }, { accountSlug, projectSlug, reelId, clipId }) {
        const url = `/accounts/${accountSlug}/${projectSlug}/video-reels/${reelId}/clips/${clipId}`;
        const response = await this.$whyApi.delete({
            url,
            mutation: "reels/SET_DELETE_CLIP",
            errorMessage: "Error deleting clip",
            notify: this.$notify
        });
        return response;
    },
};
